/*
 * Lista de importaciones.
 *
 * Lista de importaciones que muestra los detalles relacionados con estos. La lista tiene apartados
 * para mostrar todos los registros, valores de importación que se han creado en el formulario
 * de productos/piezas.
 *
 * Created on Fri Aug 04 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <!-- #region::Skeleton -->
    <template v-if="isLoadingImportsData">
      <BasicSkeleton height="150px" />
      <BasicSkeleton height="450px" />
    </template>
    <!-- #endregion::Skeleton -->

    <template v-else>

      <b-tabs v-model="selectedIndex">

        <b-tab
          title="Productos nuevos"
          lazy
          @click="onSelectedTab('new')"
        >
          <ImportsNewProductsList
            title="new"
            :download-file-base-link="'/imports/new/csv'"
          />
        </b-tab>

        <b-tab
          title="Productos seminuevos"
          lazy
          @click="onSelectedTab('used')"
        >
          <ImportsUsedProductsList
            title="used"
            :download-file-base-link="'/imports/used/csv'"
          />
        </b-tab>

      </b-tabs>

    </template>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  BTabs, BTab,
} from 'bootstrap-vue'

import ImportsNewProductsList from '@/modules/production/imports/components/ImportsNewProductsList.vue'
import ImportsUsedProductsList from '@/modules/production/imports/components/ImportsUsedProductsList.vue'

import BasicSkeleton from '@/components/cards/BasicSkeleton.vue'

export default {
  components: {
    BTab,
    BTabs,
    BasicSkeleton,
    ImportsNewProductsList,
    ImportsUsedProductsList,
  },
  data() {
    return {
      selectedIndex: 0,
      isLoadingImportsData: false,
    }
  },
  computed: {
    ...mapGetters({
      getTabs: 'imports/getTabs',
      getListsParams: 'imports/getListsParams',
      getIsEditionModeEnabled: 'imports/getIsEditionModeEnabled',
      /**
       * Filtros globales
       */
      getFilteredPerPage: 'filters/getFilteredPerPage',
      getFilteredCurrentPage: 'filters/getFilteredCurrentPage',
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredAvailability: 'filters/getFilteredAvailability',
      getFilteredETDDateRange: 'filters/getFilteredETDDateRange',
      getFilteredETADateRange: 'filters/getFilteredETADateRange',
      getFilteredOrderDateRange: 'filters/getFilteredOrderDateRange',
      getFilteredArrivalDateRange: 'filters/getFilteredArrivalDateRange',
    }),
    /**
     * Lista de pestañas
     */
    tabs: {
      get() { return this.getTabs },
    },
    /**
     * Lista de parámetros de listas
     */
    listsParams: {
      get() { return this.getListsParams },
    },
    /**
     * Indica si el modo de edición está activado
     */
    isEditionModeEnabled: {
      get() { return this.getIsEditionModeEnabled },
      set(value) { this.setIsEditionModeEnabled(value) },
    },
    /**
     * Indica el número de registros por página (paginado global)
     */
    filteredPerPage: {
      get() { return this.getFilteredPerPage },
      set(value) { this.setFilteredPerPage(value) },
    },
    /**
     * Indica la página actual (paginado global)
     */
    filteredCurrentPage: {
      get() { return this.getFilteredCurrentPage },
      set(value) { this.setFilteredCurrentPage(value) },
    },
    /**
     * Indica el término de búsqueda (paginado global)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
      set(value) { this.setFilteredSearchTerm(value) },
    },
    /**
     * Disponibilidad seleccionada (filtro en store)
     */
    filteredAvailability: {
      get() { return this.getFilteredAvailability },
      set(value) { this.setFilteredAvailability(value) },
    },
    /**
     * Fecha ETD seleccionado (filtro en store)
     */
    filteredETDDateRange: {
      get() { return this.getFilteredETDDateRange },
      set(value) { this.setFilteredETDDateRange(value) },
    },
    /**
     * Fecha ETA seleccionado (filtro en store)
     */
    filteredETADateRange: {
      get() { return this.getFilteredETADateRange },
      set(value) { this.setFilteredETADateRange(value) },
    },
    /**
     * Fecha de pedido seleccionado (filtro en store)
     */
    filteredOrderDateRange: {
      get() { return this.getFilteredOrderDateRange },
      set(value) { this.setFilteredOrderDateRange(value) },
    },
    /**
     * Fecha de llegada seleccionado (filtro en store)
     */
    filteredArrivalDateRange: {
      get() { return this.getFilteredArrivalDateRange },
      set(value) { this.setFilteredArrivalDateRange(value) },
    },
  },

  /**
  * Hook de la creación de componente
  *
  * @summary Carga la información de los filtros de columnas
  */
  async created() {
    this.isLoadingImportsData = true
    /**
     * Carga la información de los filtros de columnas
     */
    await this.loadImportsColumnsList()
    this.isLoadingImportsData = false
  },

  /**
  * Hook de la eliminación de componente
  *
  * @summary Limpia toda la informacion del módulo de importaciones
  */
  beforeDestroy() {
    this.clearImportsInfo()
  },

  methods: {
    ...mapActions({
      clearImportsInfo: 'imports/clearImportsInfo',
      loadImportsColumnsList: 'imports/loadImportsColumnsList',
      setIsEditionModeEnabled: 'imports/setIsEditionModeEnabled',
      /**
       * Filtros globales
       */
      setFilteredPerPage: 'filters/setFilteredPerPage',
      setFilteredCurrentPage: 'filters/setFilteredCurrentPage',
      setFilteredAvailability: 'filters/setFilteredAvailability',
      setFilteredSearchTerm: 'filters/setFilteredSearchTerm',
      setFilteredETDDateRange: 'filters/setFilteredETDDateRange',
      setFilteredETADateRange: 'filters/setFilteredETADateRange',
      setFilteredOrderDateRange: 'filters/setFilteredOrderDateRange',
      setFilteredArrivalDateRange: 'filters/setFilteredArrivalDateRange',
    }),

    /**
    * Evento de selección de pestaña
    *
    * @summary Establece los filtros de búsqueda de la pestaña seleccionada. Si la pestaña ya ha sido
    * seleccionada anteriormente, se establecen los filtros de búsqueda de la pestaña seleccionada, de
    * lo contrario, se establecen los filtros de búsqueda por defecto
    * @param {String} title - Título de la pestaña seleccionada
    */
    onSelectedTab(title) {
      this.isEditionModeEnabled = false

      const params = this.listsParams.find(item => item.title === title)
      this.filteredPerPage = params?.perPage || 10
      this.filteredCurrentPage = params?.currentPage || 1
      this.filteredSearchTerm = params?.searchTerm || ''
      this.filteredAvailability = params?.availabilityTerm || ''

      this.filteredETDDateRange = params?.etdDateStartTerm && params?.etdDateEndTerm
        ? { from: params?.etdDateStartTerm, to: params?.etdDateEndTerm } : null

      this.filteredETADateRange = params?.etaDateStartTerm && params?.etaDateEndTerm
        ? { from: params?.etaDateStartTerm, to: params?.etaDateEndTerm } : null

      this.filteredOrderDateRange = params?.orderDateStartTerm && params?.orderDateEndTerm
        ? { from: params?.orderDateStartTerm, to: params?.orderDateEndTerm } : null

      this.filteredArrivalDateRange = params?.arrivalDateStartTerm && params?.arrivalDateEndTerm
        ? { from: params?.arrivalDateStartTerm, to: params?.arrivalDateEndTerm } : null
    },
  },
}
</script>
